import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Eén invoerveld voor de berekening. Verwijzingen naar regels staan er als label
 * in (de omschrijving, niet de code); de rest is gewone tekst die je kunt typen.
 *
 * De waarde blijft de formulecode. Het veld rekent cursorposities om naar
 * posities in die code en biedt dezelfde selectie-API als een <input>, zodat de
 * knoppen van de editor er tekst op de cursor in kunnen zetten.
 */

export interface FormulaExpressionFieldHandle {
  readonly selectionStart: number | null;
  readonly selectionEnd: number | null;
  setSelectionRange: (start: number, end: number) => void;
  focus: () => void;
}

interface FormulaExpressionFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Code → omschrijving van de regels waarnaar verwezen mag worden. */
  labels: Map<string, string>;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

const CHIP_CLASS =
  'mx-0.5 inline-block max-w-56 truncate rounded border bg-muted px-2 py-0.5 align-middle text-xs font-semibold text-foreground';

const isChip = (node: Node): node is HTMLElement =>
  node instanceof HTMLElement && Boolean(node.dataset.code);

// Onzichtbaar teken rond labels: zonder tekst ervoor of erachter kan Chrome de
// cursor daar niet neerzetten en kun je vóór een label niets typen.
const CARET_ANCHOR = '\u200b';

const cleanText = (text: string) => text.replace(/\u200b/g, '').replace(/\u00a0/g, ' ');

const nodeText = (node: Node): string =>
  isChip(node) ? (node.dataset.code ?? '') : cleanText(node.textContent ?? '');

function serialize(root: HTMLElement): string {
  return Array.from(root.childNodes).map(nodeText).join('');
}

/** Positie in de formulecode voor een DOM-positie binnen het veld. */
function offsetOf(root: HTMLElement, node: Node, offset: number): number {
  if (node === root) {
    return Array.from(root.childNodes)
      .slice(0, offset)
      .reduce((total, child) => total + nodeText(child).length, 0);
  }
  let total = 0;
  for (const child of Array.from(root.childNodes)) {
    if (child === node || child.contains(node)) {
      return (
        total +
        (isChip(child)
          ? nodeText(child).length
          : cleanText((child.textContent ?? '').slice(0, offset)).length)
      );
    }
    total += nodeText(child).length;
  }
  return total;
}

/** DOM-positie voor een positie in de formulecode. Binnen een label: erachter. */
function positionOf(root: HTMLElement, target: number): { node: Node; offset: number } {
  let total = 0;
  const children = Array.from(root.childNodes);
  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    const length = nodeText(child).length;
    if (isChip(child)) {
      if (target <= total) return { node: root, offset: index };
      if (target < total + length) return { node: root, offset: index + 1 };
    } else if (target <= total + length) {
      // Tel alleen echte tekens; het onzichtbare anker telt niet mee.
      const raw = child.textContent ?? '';
      let remaining = target - total;
      let offset = 0;
      while (offset < raw.length && (remaining > 0 || raw[offset] === CARET_ANCHOR)) {
        if (raw[offset] !== CARET_ANCHOR) remaining -= 1;
        offset += 1;
      }
      return { node: child, offset };
    }
    total += length;
  }
  return { node: root, offset: children.length };
}

function render(root: HTMLElement, value: string, labels: Map<string, string>) {
  root.replaceChildren();
  const pattern = /[A-Za-z_][A-Za-z0-9_]*/g;
  let last = 0;
  for (const match of value.matchAll(pattern)) {
    const label = labels.get(match[0]);
    if (!label) continue;
    const start = match.index ?? 0;
    root.append(start > last ? value.slice(last, start) : CARET_ANCHOR);
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.dataset.code = match[0];
    chip.title = `${label} · ${match[0]}`;
    chip.className = CHIP_CLASS;
    chip.textContent = label;
    root.append(chip);
    last = start + match[0].length;
  }
  if (last < value.length) root.append(value.slice(last));
  else if (last > 0) root.append(CARET_ANCHOR);
}

export const FormulaExpressionField = forwardRef<
  FormulaExpressionFieldHandle,
  FormulaExpressionFieldProps
>(function FormulaExpressionField(
  { value, onChange, labels, invalid, placeholder, className, id, 'aria-label': ariaLabel },
  ref
) {
  const rootRef = useRef<HTMLDivElement>(null);
  // De waarde die het veld zelf het laatst doorgaf. Komt die terug, dan staat de
  // DOM al goed en bouwen we niets opnieuw op; dat zou de cursor laten springen.
  const emitted = useRef<string | null>(null);
  // Klik je op een knop van de editor, dan verlaat de focus het veld. De knop
  // moet dan nog weten waar de cursor stond.
  const saved = useRef<{ start: number; end: number } | null>(null);

  const selectionOffsets = () => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
    return {
      start: offsetOf(root, range.startContainer, range.startOffset),
      end: offsetOf(root, range.endContainer, range.endOffset),
    };
  };

  const currentSelection = () => {
    // Zonder focus is de DOM-selectie onbetrouwbaar (na opnieuw opbouwen staat
    // die op 0); dan geldt de laatst bekende cursorpositie.
    const live = document.activeElement === rootRef.current ? selectionOffsets() : null;
    if (live) return live;
    if (!saved.current) return null;
    const length = (emitted.current ?? value).length;
    return {
      start: Math.min(saved.current.start, length),
      end: Math.min(saved.current.end, length),
    };
  };

  const setSelectionRange = (start: number, end: number) => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection) return;
    saved.current = { start, end };
    const from = positionOf(root, start);
    const to = positionOf(root, end);
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  useImperativeHandle(ref, () => ({
    get selectionStart() {
      return currentSelection()?.start ?? null;
    },
    get selectionEnd() {
      return currentSelection()?.end ?? null;
    },
    setSelectionRange,
    focus: () => rootRef.current?.focus(),
  }));

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || value === emitted.current) return;
    const focused = document.activeElement === root;
    const caret = focused ? selectionOffsets() : null;
    render(root, value, labels);
    emitted.current = value;
    if (caret) setSelectionRange(caret.start, caret.end);
    // labels wisselt alleen als de regels wijzigen; dan opnieuw opbouwen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || document.activeElement === root) return;
    render(root, value, labels);
    emitted.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels]);

  const emit = () => {
    const root = rootRef.current;
    if (!root) return;
    const next = serialize(root);
    // Een leeg veld moet echt leeg zijn, anders verschijnt de placeholder niet.
    if (!next) root.replaceChildren();
    emitted.current = next;
    onChange(next);
  };

  const insertText = (text: string) => {
    const root = rootRef.current;
    const offsets = selectionOffsets();
    if (!root || !offsets) return;
    const current = serialize(root);
    const next = current.slice(0, offsets.start) + text + current.slice(offsets.end);
    const caret = offsets.start + text.length;
    render(root, next, labels);
    emitted.current = next;
    setSelectionRange(caret, caret);
    onChange(next);
  };

  return (
    <div
      ref={rootRef}
      id={id}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline="false"
      aria-invalid={invalid}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      data-placeholder={placeholder}
      className={cn(
        'min-h-11 whitespace-pre-wrap break-words leading-8 outline-none',
        'empty:before:pointer-events-none empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]',
        className
      )}
      onInput={emit}
      onBlur={() => {
        // Getypte codes worden pas bij het verlaten een label, zodat de cursor
        // tijdens het typen niet verspringt.
        const root = rootRef.current;
        if (!root) return;
        saved.current = selectionOffsets();
        render(root, value, labels);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.preventDefault();
      }}
      onPaste={(event) => {
        event.preventDefault();
        insertText(event.clipboardData.getData('text/plain').replace(/\s*\n\s*/g, ' '));
      }}
      onDrop={(event) => event.preventDefault()}
    />
  );
});

export default FormulaExpressionField;
